import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import Menu from '../Menu';
import api from '../../api';
import './Marking.scss';
import {
    buildMarkingScanPayload,
    buildNativeMarkingDeepLink,
    isNativeMarkingScannerAvailable,
    markingRequestErrorMessage,
    NativeMarkingWindow,
    readActiveMarkingCompany,
} from './markingSupport';

declare global {
    interface Window {
        ReactNativeWebView?: {
            postMessage: (message: string) => void;
        };
        __CC_NATIVE_CAPABILITIES__?: {
            supportsMarkingDataMatrixScanner?: boolean;
        };
    }
}

type MarkingSession = {
    token: string;
    status: string;
    lineId?: string | null;
    itemName?: string | null;
    unitIndex?: number | null;
    goodTitle?: string | null;
    gtin?: string | null;
    expectedGtin?: string | null;
    gtinTail?: string | null;
    expiresAt?: string | null;
    terminal?: {
        id?: number | string | null;
        key?: string | null;
        title?: string | null;
    } | null;
};

type PendingScan = {
    requestId: string;
    token: string;
};

type NativeScanDetail = {
    requestId?: string | null;
    sessionToken?: string | null;
    format?: string | null;
    dataMatrix?: string | null;
};

const RESULT_EVENT = 'chio:native-barcode-scan-result';
const CANCEL_EVENT = 'chio:native-barcode-scan-cancel';

function formatExpiresAt(value?: string | null): string {
    if (!value) {
        return '';
    }
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
        return '';
    }
    return date.toLocaleTimeString('ru-RU', {hour: '2-digit', minute: '2-digit'});
}

function formatGtinTail(session: MarkingSession): string {
    const suppliedTail = typeof session.gtinTail === 'string' ? session.gtinTail : '';
    const fullGtin = typeof session.expectedGtin === 'string'
        ? session.expectedGtin
        : typeof session.gtin === 'string' ? session.gtin : '';
    const normalized = (suppliedTail || fullGtin).replace(/\s+/g, '');

    return normalized ? 'GTIN …' + normalized.slice(-6) : '';
}

function createRequestId(): string {
    return Date.now().toString(36) + '-' + Math.random().toString(36).slice(2);
}

export default function Marking() {
    const [sessions, setSessions] = useState<MarkingSession[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string>('');
    const [status, setStatus] = useState<string>('');
    const [submittingToken, setSubmittingToken] = useState<string>('');
    const [pendingScan, setPendingScan] = useState<PendingScan | null>(null);
    const pendingScanRef = useRef<PendingScan | null>(null);
    const activeCompany = useMemo(() => readActiveMarkingCompany(), []);
    const companyId = activeCompany.id;
    const [nativeAvailable, setNativeAvailable] = useState<boolean>(() => isNativeMarkingScannerAvailable());

    useEffect(() => {
        const refreshCapability = () => setNativeAvailable(isNativeMarkingScannerAvailable());
        window.addEventListener('cc_native_session_bootstrap', refreshCapability);
        window.addEventListener('pageshow', refreshCapability);
        refreshCapability();

        return () => {
            window.removeEventListener('cc_native_session_bootstrap', refreshCapability);
            window.removeEventListener('pageshow', refreshCapability);
        };
    }, []);

    const loadSessions = useCallback(async (silent: boolean = false) => {
        if (!silent) {
            setLoading(true);
            setError('');
        }
        try {
            const response = await api.get('/widget/staff/v2/marking/sessions/pending', {
                params: companyId ? {companyId} : {},
                withCredentials: true,
            });
            const items = Array.isArray(response.data?.items)
                ? response.data.items
                : Array.isArray(response.data)
                    ? response.data
                    : [];
            setSessions(items);
        } catch (requestError) {
            if (!silent) {
                setError(markingRequestErrorMessage(requestError, 'load'));
            }
        } finally {
            if (!silent) {
                setLoading(false);
            }
        }
    }, [companyId]);

    const submitScan = useCallback(async (token: string, dataMatrix: string, requestId: string) => {
        setSubmittingToken(token);
        setError('');
        setStatus('');
        try {
            await api.post('/widget/staff/v2/marking/sessions/' + encodeURIComponent(token) + '/scan', buildMarkingScanPayload(
                dataMatrix,
                requestId,
            ), {
                params: companyId ? {companyId} : {},
                withCredentials: true,
            });
            setStatus('Код отправлен на терминал');
            await loadSessions(true);
        } catch (requestError) {
            setError(markingRequestErrorMessage(requestError, 'submit'));
        } finally {
            setSubmittingToken('');
            setPendingScan(null);
            pendingScanRef.current = null;
        }
    }, [companyId, loadSessions]);

    useEffect(() => {
        loadSessions();
    }, [loadSessions]);

    useEffect(() => {
        const poll = () => {
            if (document.visibilityState === 'visible' && !pendingScanRef.current && !submittingToken) {
                void loadSessions(true);
            }
        };
        const timer = window.setInterval(poll, 5000);
        document.addEventListener('visibilitychange', poll);

        return () => {
            window.clearInterval(timer);
            document.removeEventListener('visibilitychange', poll);
        };
    }, [loadSessions, submittingToken]);

    useEffect(() => {
        const handleResult = (event: Event) => {
            const detail = ((event as CustomEvent).detail || {}) as NativeScanDetail;
            const active = pendingScanRef.current;
            if (!active || detail.requestId !== active.requestId) {
                return;
            }
            if (detail.sessionToken && detail.sessionToken !== active.token) {
                return;
            }
            if (detail.format && detail.format !== 'datamatrix') {
                pendingScanRef.current = null;
                setPendingScan(null);
                setError('Сканер вернул не DataMatrix. Обычный QR-код не подходит.');
                return;
            }
            const dataMatrix = typeof detail.dataMatrix === 'string' ? detail.dataMatrix : '';
            if (dataMatrix === '') {
                pendingScanRef.current = null;
                setPendingScan(null);
                setError('Сканер не вернул код маркировки');
                return;
            }
            void submitScan(active.token, dataMatrix, active.requestId);
        };

        const handleCancel = (event: Event) => {
            const detail = ((event as CustomEvent).detail || {}) as NativeScanDetail;
            const active = pendingScanRef.current;
            if (!active || detail.requestId !== active.requestId) {
                return;
            }
            pendingScanRef.current = null;
            setPendingScan(null);
            setStatus('Сканирование отменено');
        };

        window.addEventListener(RESULT_EVENT, handleResult);
        window.addEventListener(CANCEL_EVENT, handleCancel);
        return () => {
            window.removeEventListener(RESULT_EVENT, handleResult);
            window.removeEventListener(CANCEL_EVENT, handleCancel);
        };
    }, [submitScan]);

    const openScanner = (session: MarkingSession) => {
        const nativeWindow = window as NativeMarkingWindow;
        const requestId = createRequestId();
        if (!isNativeMarkingScannerAvailable(nativeWindow)) {
            setNativeAvailable(false);
            if (!companyId) {
                setError('Выберите салон перед сканированием DataMatrix.');
                return;
            }
            setError('');
            setStatus('Открываем нативный DataMatrix-сканер в приложении Chio Chio');
            window.location.assign(buildNativeMarkingDeepLink(
                requestId,
                session.token,
                companyId,
                window.location.href,
            ));
            return;
        }
        const nextPending = {requestId, token: session.token};
        pendingScanRef.current = nextPending;
        setPendingScan(nextPending);
        setError('');
        setStatus('Открываем сканер');
        try {
            nativeWindow.ReactNativeWebView!.postMessage(JSON.stringify({
                v: 1,
                type: 'open_marking_datamatrix_scanner',
                data: {
                    requestId,
                    sessionToken: session.token,
                    source: 'staff_marking',
                },
            }));
        } catch {
            pendingScanRef.current = null;
            setPendingScan(null);
            setError('Не удалось открыть DataMatrix-сканер. Перезапустите приложение и повторите попытку.');
        }
    };

    return (
        <div className='marking-page'>
            <div className='marking-content'>
                <div className='marking-header'>
                    <div>
                        <div className='marking-title'>Маркировка</div>
                        <div className='marking-company'>Салон: {activeCompany.label || (companyId ? '#' + companyId : 'не выбран')}</div>
                    </div>
                    <button type='button' className='marking-refresh' onClick={() => void loadSessions()} disabled={loading}>
                        Обновить
                    </button>
                </div>

                {!nativeAvailable ? (
                    <div className='marking-status error'>
                        По кнопке сканирования откроется актуальное мобильное приложение. Камера браузера и обычный QR-сканер не используются.
                    </div>
                ) : null}
                {error ? <div className='marking-status error'>{error}</div> : null}
                {status ? <div className='marking-status'>{status}</div> : null}

                {loading ? (
                    <div className='marking-empty'>Загрузка</div>
                ) : sessions.length === 0 ? (
                    <div className='marking-empty'>Активных сессий нет</div>
                ) : (
                    <div className='marking-list'>
                        {sessions.map((session) => {
                            const terminalTitle = session.terminal?.title || session.terminal?.key || 'Терминал';
                            const expiresAt = formatExpiresAt(session.expiresAt);
                            const gtinTail = formatGtinTail(session);
                            const isSubmitting = submittingToken === session.token;
                            const isPending = pendingScan?.token === session.token;
                            return (
                                <div className='marking-session' key={session.token}>
                                    <div className='marking-session-main'>
                                        <div className='marking-session-title'>{session.itemName || session.goodTitle || 'Маркированный товар'}</div>
                                        <div className='marking-session-meta'>
                                            {terminalTitle}
                                            {session.unitIndex != null ? ' · единица ' + session.unitIndex : ''}
                                            {gtinTail ? ' · ' + gtinTail : ''}
                                            {expiresAt ? ' · до ' + expiresAt : ''}
                                        </div>
                                    </div>
                                    <button
                                        type='button'
                                        className='marking-scan-button'
                                        disabled={isSubmitting || !!pendingScan}
                                        onClick={() => openScanner(session)}
                                    >
                                        {isSubmitting ? 'Отправляем' : isPending ? 'Сканирование' : nativeAvailable ? 'Сканировать' : 'Открыть приложение'}
                                    </button>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
            <Menu/>
        </div>
    );
}
